import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input, Toggle } from '../design-system';
import { DatePickerField } from '../DatePickerField';
import { ProductSelectorModal } from '../catalogues/ProductSelectorModal';
import { DataListColumn, apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface DataListRepeaterFieldProps {
  column: DataListColumn;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  businessId: string;
}

interface OrdersItemRow {
  productId: string;
  quantity: number;
  name?: string;
  sku?: string;
  price?: number;
}

/**
 * Renders a 'repeater' column: an add/remove-as-needed array of structured
 * rows. Two modes: the generic scalar sub-field grid (column.repeater.fields,
 * for any custom-list repeater), and Orders' special-cased product picker
 * (column.repeater.kind === 'system-orders-items') which reuses
 * ProductSelectorModal — the same picker AddOrderScreen.tsx already uses for
 * manual order-item entry — instead of a blank text-entry row.
 */
export const DataListRepeaterField: React.FC<DataListRepeaterFieldProps> = ({ column, value, onChange, error, businessId }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const constraints = column.constraints || {};

  if (column.repeater?.kind === 'system-orders-items') {
    return <OrdersItemsRepeater column={column} value={value} onChange={onChange} error={error} businessId={businessId} />;
  }

  const rows: Array<Record<string, any>> = Array.isArray(value) ? value : [];
  const fields = column.repeater?.fields || [];

  const updateRow = (index: number, fieldId: string, fieldValue: any) => {
    const next = rows.map((row, i) => (i === index ? { ...row, [fieldId]: fieldValue } : row));
    onChange(next);
  };
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  return (
    <View style={styles.field}>
      <View style={[styles.labelRow, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.label, { textAlign: rtl.textStart }]}>{column.name}</Text>
        {constraints.required && <Text style={styles.requiredStar}> *</Text>}
      </View>

      {rows.map((row, index) => (
        <View key={index} style={styles.row}>
          <View style={[styles.rowHeader, { flexDirection: rtl.rowDirection }]}>
            <Text style={styles.rowIndex}>{t('dataLists.field.repeaterRow', { number: index + 1 })}</Text>
            <TouchableOpacity onPress={() => removeRow(index)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close-circle" size={20} color={colors.error} />
            </TouchableOpacity>
          </View>
          {fields.map((field) => (
            <View key={field.id} style={styles.subField}>
              {field.type === 'text' && (
                <Input
                  label={field.name}
                  value={row[field.id] ?? ''}
                  onChangeText={(v) => updateRow(index, field.id, v)}
                />
              )}
              {field.type === 'number' && (
                <Input
                  label={field.name}
                  value={row[field.id] != null ? String(row[field.id]) : ''}
                  onChangeText={(v) => updateRow(index, field.id, v === '' ? undefined : Number(v))}
                  keyboardType="numeric"
                />
              )}
              {field.type === 'boolean' && (
                <Toggle label={field.name} value={!!row[field.id]} onValueChange={(v) => updateRow(index, field.id, v)} />
              )}
              {field.type === 'date' && (
                <DatePickerField
                  label={field.name}
                  value={row[field.id] ? new Date(row[field.id]) : null}
                  onChange={(d) => updateRow(index, field.id, d ? d.toISOString() : undefined)}
                />
              )}
            </View>
          ))}
        </View>
      ))}

      <TouchableOpacity style={[styles.addRowButton, { flexDirection: rtl.rowDirection }]} onPress={addRow}>
        <Ionicons name="add" size={18} color={colors.primary as string} />
        <Text style={styles.addRowText}>{t('dataLists.field.addRow')}</Text>
      </TouchableOpacity>

      {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
    </View>
  );
};

const OrdersItemsRepeater: React.FC<DataListRepeaterFieldProps> = ({ column, value, onChange, error }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const constraints = column.constraints || {};
  const rows: OrdersItemRow[] = Array.isArray(value) ? value : [];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingProductIds, setLoadingProductIds] = useState<Set<string>>(new Set());

  // Rows can arrive with only {productId, quantity} (e.g. loaded from a saved
  // record) — backfill display name/sku/price lazily so the row doesn't show
  // a blank line while still storing only the ids that matter.
  useEffect(() => {
    const missing = rows.filter((r) => r.productId && r.productId !== 'external' && !r.name && !loadingProductIds.has(r.productId));
    if (missing.length === 0) return;
    setLoadingProductIds((prev) => new Set([...prev, ...missing.map((r) => r.productId)]));
    (async () => {
      for (const row of missing) {
        try {
          const res = await apiService.getProductById(row.productId);
          if (res.statusCode === 1000 && res.data) {
            onChange(rows.map((r) => (r.productId === row.productId ? { ...r, name: res.data.name, sku: res.data.sku, price: res.data.price } : r)));
          }
        } catch {
          // best-effort — row still functions with just the id/quantity
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => r.productId).join(',')]);

  const updateQuantity = (index: number, quantity: string) => {
    const n = Number(quantity);
    onChange(rows.map((r, i) => (i === index ? { ...r, quantity: quantity === '' ? undefined : n } : r)));
  };
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const handleProductsSelected = (selectedProductIds: string[]) => {
    const existingIds = new Set(rows.map((r) => r.productId));
    const newRows = selectedProductIds.filter((id) => !existingIds.has(id)).map((id) => ({ productId: id, quantity: 1 }));
    onChange([...rows, ...newRows]);
    setPickerOpen(false);
  };

  return (
    <View style={styles.field}>
      <View style={[styles.labelRow, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.label, { textAlign: rtl.textStart }]}>{column.name}</Text>
        {constraints.required && <Text style={styles.requiredStar}> *</Text>}
      </View>

      {rows.map((row, index) => (
        <View key={`${row.productId}-${index}`} style={[styles.orderItemRow, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.orderItemInfo}>
            <Text style={styles.orderItemName} numberOfLines={1}>{row.name || row.productId}</Text>
            {row.price != null && <Text style={styles.orderItemPrice}>{row.price}</Text>}
          </View>
          <TextInput
            style={styles.quantityInput}
            value={row.quantity != null ? String(row.quantity) : ''}
            onChangeText={(v) => updateQuantity(index, v)}
            keyboardType="numeric"
            placeholder={t('dataLists.field.quantityPlaceholder')}
            placeholderTextColor={colors.textLight as string}
          />
          <TouchableOpacity onPress={() => removeRow(index)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close-circle" size={20} color={colors.error} />
          </TouchableOpacity>
        </View>
      ))}

      <TouchableOpacity style={[styles.addRowButton, { flexDirection: rtl.rowDirection }]} onPress={() => setPickerOpen(true)}>
        <Ionicons name="add" size={18} color={colors.primary as string} />
        <Text style={styles.addRowText}>{t('dataLists.field.addProducts')}</Text>
      </TouchableOpacity>

      {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}

      <ProductSelectorModal
        visible={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleProductsSelected}
        title={t('dataLists.field.selectProductsTitle')}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  field: { marginBottom: spacing.md },
  labelRow: { alignItems: 'center', marginBottom: spacing.xs },
  label: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.text.primary as any, fontFamily: typography.fontFamily.semibold },
  requiredStar: { color: colors.error as string },
  row: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  rowHeader: { justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs },
  rowIndex: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  subField: { marginBottom: spacing.xs },
  addRowButton: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    borderStyle: 'dashed',
  },
  addRowText: { fontSize: typography.fontSize.sm, color: colors.primary as string, fontFamily: typography.fontFamily.medium },
  errorText: { fontSize: typography.fontSize.xs, color: colors.error as string, marginTop: spacing.xs },
  orderItemRow: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    marginBottom: spacing.xs,
  },
  orderItemInfo: { flex: 1 },
  orderItemName: { fontSize: typography.fontSize.sm, color: colors.text.primary as any, fontFamily: typography.fontFamily.medium },
  orderItemPrice: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  quantityInput: {
    width: 60,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    textAlign: 'center',
    color: colors.text.primary as any,
    fontFamily: typography.fontFamily.regular,
  },
});
